/**
 * Wizard card for the ads_conversions step (Slice 7 — second attribution slice).
 *
 * The admin enters the client's Google Ads customer ID; `create_conversions`
 * fetches the account-level AW conversion ID and creates the standard website
 * conversion actions under that account (linked under the platform MCC). The AW
 * ID is stored under `seo_google_ads_conversion_id`, which wires it into the
 * viewer's Google Ads tag; the per-action labels are stored for Slice 9 (GTM).
 *
 * No secret is handled here — the Ads credentials are platform env vars; the
 * customer ID / AW ID / labels are identifiers, not credentials. So the customer
 * ID reads back into the field. The MCC-link requirement surfaces as a
 * `waiting_on_client` task with a readable message via StepCard's error slot.
 */
import { useEffect, useMemo, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useQuery } from '@tanstack/react-query'
import { CheckCircle2, Loader2, RefreshCw, Sparkles } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useToast } from '@/hooks/use-toast'
import { supabase } from '@/lib/supabase'
import type { OnboardingTask } from '@limo/database'
import { useInvokeOnboardingStep } from '@/hooks/useOnboarding'
import { StepCard } from './StepCard'
import { GoogleAdsAccountBadge } from './GoogleAdsAccountBadge'

interface GoogleAdsCardProps {
  task: OnboardingTask
  accountId: string
  runId: string
}

interface ConversionActionResult {
  name: string
  label: string | null
  resourceName: string
  reused: boolean
}

interface GoogleAdsResult {
  awConversionId?: string
  customerId?: string
  actions?: ConversionActionResult[]
  validateOnly?: boolean
}

const ACTION_NAMES = [
  'get_vehicle_quotes',
  'purchase',
  'select_vehicle',
  'provide_contact_info',
  'request_quote',
  'generate_lead',
] as const

const formSchema = z.object({
  adsCustomerId: z
    .string()
    .trim()
    .transform((v) => v.replace(/-/g, ''))
    .pipe(z.string().regex(/^\d{10}$/, 'Customer ID must be 10 digits (no dashes)')),
})

type FormData = z.infer<typeof formSchema>

export function GoogleAdsCard({ task, accountId, runId }: GoogleAdsCardProps) {
  const { toast } = useToast()
  const invokeStep = useInvokeOnboardingStep(runId)

  const result = (task.result ?? {}) as GoogleAdsResult
  const params = (task.params ?? {}) as { adsCustomerId?: string }
  const isDone = task.status === 'done'

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: { adsCustomerId: '' },
  })

  // Read back saved values so the field + IDs survive a refresh (none secret).
  const { data: saved } = useQuery({
    queryKey: ['onboarding-google-ads-settings', accountId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('account_settings')
        .select('key, value')
        .eq('account_id', accountId)
        .in('key', [
          'google_ads_customer_id',
          'seo_google_ads_conversion_id',
          ...ACTION_NAMES.map((n) => `gads_label_${n}`),
        ])
      if (error) throw new Error(error.message)
      const map: Record<string, string> = {}
      for (const row of data ?? []) {
        if (row.value != null) map[row.key] = row.value
      }
      return map
    },
  })

  // Seed once: saved customer id, else the run's adsCustomerId param.
  const seeded = useRef(false)
  useEffect(() => {
    if (!saved || seeded.current) return
    seeded.current = true
    form.reset({
      adsCustomerId: saved['google_ads_customer_id'] ?? params.adsCustomerId ?? '',
    })
  }, [saved, params.adsCustomerId, form])

  const awConversionId =
    result.awConversionId ?? saved?.['seo_google_ads_conversion_id'] ?? ''

  // Action rows for the done state: prefer the run result, else the saved labels.
  const actionRows = useMemo(() => {
    if (result.actions?.length) return result.actions
    return ACTION_NAMES.map((name) => ({
      name,
      label: saved?.[`gads_label_${name}`] ?? null,
      resourceName: '',
      reused: true,
    }))
  }, [result.actions, saved])

  const createConversions = form.handleSubmit((values) => {
    invokeStep.mutate(
      {
        functionName: 'onboard-google-ads',
        accountId,
        params: { action: 'create_conversions', adsCustomerId: values.adsCustomerId },
      },
      {
        onError: (error) =>
          toast({
            title: 'Google Ads provisioning failed',
            description: error.message,
            variant: 'destructive',
          }),
      }
    )
  })

  return (
    <StepCard
      task={task}
      manualFallback={
        <div className="space-y-2">
          <p>
            <strong>Before running:</strong> the client must link their Google
            Ads account under the platform MCC — in Google Ads → Admin → Account
            access → Managers, accept the manager link. The conversion actions
            are created under the client&rsquo;s account (they own them).
          </p>
          <p>
            Platform prerequisites: an approved Google Ads developer token, an
            OAuth refresh token, and the MCC — set as edge-function secrets.
          </p>
          <p>
            Manual path: create the conversion actions in Google Ads → Tools →
            Conversions and paste the AW conversion ID into Settings &gt; SEO
            &amp; Marketing &gt; Google Ads Conversion ID.
          </p>
        </div>
      }
    >
      <GoogleAdsAccountBadge accountId={accountId} />
      {isDone ? (
        <div className="space-y-2 text-sm">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-green-600" />
            <span>
              Google Ads conversions provisioned — AW ID{' '}
              <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">
                {awConversionId || '—'}
              </code>
            </span>
          </div>
          <ul className="space-y-0.5 text-xs text-muted-foreground">
            {actionRows.map((a) => (
              <li key={a.name}>
                <span className="font-mono">{a.name}</span>
                {a.label ? ` → ${a.label}` : ' → (label pending)'}
                {a.reused ? ' (reused)' : ''}
              </li>
            ))}
          </ul>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => createConversions()}
            disabled={invokeStep.isPending}
          >
            {invokeStep.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="mr-2 h-4 w-4" />
            )}
            Re-run / reconcile
          </Button>
        </div>
      ) : (
        <form onSubmit={createConversions} className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Ensure the client&rsquo;s Ads account is linked under the platform
            MCC (see Manual fallback), then create the conversion actions.
          </p>
          <div className="space-y-1">
            <Label htmlFor="ads-customer">Google Ads customer ID</Label>
            <Input
              id="ads-customer"
              placeholder="e.g. 1234567890"
              {...form.register('adsCustomerId')}
            />
            {form.formState.errors.adsCustomerId && (
              <p className="text-xs text-red-600">
                {form.formState.errors.adsCustomerId.message}
              </p>
            )}
          </div>
          <Button type="submit" size="sm" disabled={invokeStep.isPending}>
            {invokeStep.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Sparkles className="mr-2 h-4 w-4" />
            )}
            Create conversion actions
          </Button>
        </form>
      )}
    </StepCard>
  )
}
